import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DescriptionOutlined,
  SchoolOutlined,
  CalendarTodayOutlined,
  DownloadOutlined,
} from "@mui/icons-material";
import api from "../services/api";

export default function Transcript() {
  const { studentCourseId } = useParams();
  const [transcriptData, setTranscriptData] = useState(null);
  const [studentCourses, setStudentCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(studentCourseId || "");
  const [courseTerms, setCourseTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Fetch student courses on component mount
  useEffect(() => {
    const fetchStudentCourses = async () => {
      try {
        const authToken = sessionStorage.getItem("token");
        if (!authToken) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        // First get student profile to get student ID
        const profileResponse = await api({
          url: `/api/user/student/profile`,
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (profileResponse.status === 200) {
          const studentId = profileResponse.data.data.id;

          // Then fetch student courses
          const coursesResponse = await api({
            url: `/api/student/${studentId}/courses`,
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          });

          if (coursesResponse.status === 200) {
            setStudentCourses(coursesResponse.data.data || []);
            // If studentCourseId is provided in URL, use it
            if (studentCourseId) {
              setSelectedCourse(studentCourseId);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching student courses:", error);
        setError("Failed to fetch student courses");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentCourses();
  }, [studentCourseId]);

  // Fetch course terms when course is selected
  useEffect(() => {
    if (!selectedCourse) {
      setCourseTerms([]);
      setSelectedTerm("");
      return;
    }

    const fetchCourseTerms = async () => {
      try {
        const authToken = sessionStorage.getItem("token");
        if (!authToken) return;

        // Extract course ID from selectedCourse (student course ID)
        // First get the course info from student courses
        const selectedCourseInfo = studentCourses.find(
          (course) => course.id.toString() === selectedCourse.toString()
        );

        if (selectedCourseInfo) {
          const response = await api({
            url: `/api/course/${selectedCourseInfo.course_id}/terms`,
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          });

          if (response.status === 200) {
            setCourseTerms(response.data.data || []);
            // Reset selected term when course changes
            setSelectedTerm("");
          }
        }
      } catch (error) {
        console.error("Error fetching course terms:", error);
        setCourseTerms([]);
      }
    };

    fetchCourseTerms();
  }, [selectedCourse, studentCourses]);

  // Fetch transcript data when course is selected
  useEffect(() => {
    if (!selectedCourse) return;

    const fetchTranscript = async () => {
      try {
        setLoading(true);
        setError(null);

        const authToken = sessionStorage.getItem("token");
        if (!authToken) {
          setError("No authentication token found");
          setLoading(false);
          return;
        }

        // Build URL with optional courseTermId parameter
        let url = `/api/report/transcript/${selectedCourse}`;
        if (selectedTerm) {
          url += `?courseTermId=${selectedTerm}`;
        }

        const response = await api({
          url: url,
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (response.status === 200) {
          setTranscriptData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching transcript:", error);
        setError("Failed to fetch transcript data");
      } finally {
        setLoading(false);
      }
    };

    fetchTranscript();
  }, [selectedCourse, selectedTerm]);

  const groupScoresByTerm = (scores) => {
    // Backend handles filtering when courseTermId is provided
    // Frontend just groups the returned scores by term
    const grouped = {};
    scores.forEach((score) => {
      if (!grouped[score.course_term]) {
        grouped[score.course_term] = [];
      }
      grouped[score.course_term].push(score);
    });
    return grouped;
  };

  const handleCourseChange = (event) => {
    setSelectedCourse(event.target.value);
  };

  const handleTermChange = (event) => {
    setSelectedTerm(event.target.value);
  };

  const handleDownloadPDF = async () => {
    if (!selectedCourse) {
      alert("Please select a course first");
      return;
    }

    try {
      setDownloading(true);
      const authToken = sessionStorage.getItem("token");

      if (!authToken) {
        alert("Authentication token not found");
        return;
      }

      // Build URL with optional courseTermId parameter
      let url;
      if (selectedTerm) {
        url = `/api/report/result-slip/download?studentCourseId=${selectedCourse}&courseTermId=${selectedTerm}`;
      } else {
        url = `/api/report/transcript/download?studentCourseId=${selectedCourse}`;
      }

      const response = await api({
        url: url,
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: "blob", // Important for file downloads
      });

      // Create blob link to download
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;

      // Get filename from response headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = selectedTerm ? "result-slip.pdf" : "transcript.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download transcript. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4 space-x-3">
          <div className="flex items-center space-x-3">
            <DescriptionOutlined
              className="text-blue-600"
              sx={{ fontSize: 28 }}
            />
            <h1 className="text-2xl font-bold text-gray-900">
              Academic Transcript
            </h1>
          </div>

          {/* Download Button */}
          {selectedCourse && (
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <DownloadOutlined className="mr-2" sx={{ fontSize: 18 }} />
                  Download {selectedTerm ? "Result Slip" : "Transcript"}
                </>
              )}
            </button>
          )}
        </div>

        {/* Course Selection */}
        {studentCourses.length > 0 && (
          <div className="mb-4 space-y-4">
            <div>
              <label
                htmlFor="course-select"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select Course to View Transcript:
              </label>
              <select
                id="course-select"
                value={selectedCourse}
                onChange={handleCourseChange}
                className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Please select a course --</option>
                {studentCourses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.course_name} - {course.branch_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Term Filter */}
            {selectedCourse && courseTerms.length > 0 && (
              <div>
                <label
                  htmlFor="term-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Filter by Term (Optional):
                </label>
                <select
                  id="term-select"
                  value={selectedTerm}
                  onChange={handleTermChange}
                  className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- All Terms --</option>
                  {courseTerms.map((term) => (
                    <option key={term.id} value={term.id}>
                      Year {term.year} Term {term.term}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Course Selection Prompt */}
      {!selectedCourse && studentCourses.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8">
          <div className="text-center">
            <DescriptionOutlined className="mx-auto h-16 w-16 text-blue-400 mb-4" />
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Select a Course
            </h3>
            <p className="text-blue-700">
              Please select a course from the dropdown above to view your
              academic transcript.
            </p>
          </div>
        </div>
      )}

      {/* No Courses Available */}
      {!loading && studentCourses.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
          <div className="text-center">
            <SchoolOutlined className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Courses Found
            </h3>
            <p className="text-gray-600">
              No courses are available for your account. Please contact your
              administrator.
            </p>
          </div>
        </div>
      )}

      {transcriptData && (
        <>
          {/* Student Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <SchoolOutlined className="mr-2 text-blue-600" />
              Student Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Admission Number
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {transcriptData.admission_no}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Student Name
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {transcriptData.student_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Course
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {transcriptData.course_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Branch
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {transcriptData.branch_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Registration Date
                </label>
                <p className="mt-1 text-sm text-gray-900 flex items-center">
                  <CalendarTodayOutlined
                    className="mr-1 text-gray-400"
                    sx={{ fontSize: 16 }}
                  />
                  {transcriptData.registration_date}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Classroom
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {transcriptData.classroom_name}
                </p>
              </div>
            </div>
          </div>

          {/* Academic Performance */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Academic Performance
            </h2>

            {/* Overall Performance - Only show if scores exist */}
            {transcriptData.scores && transcriptData.scores.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Total Average Score
                    </label>
                    <p className="mt-1 text-lg font-semibold text-blue-600">
                      {transcriptData.total_average_score &&
                      transcriptData.total_average_score !== "-"
                        ? transcriptData.total_average_score
                        : "Not Available"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Overall Grade
                    </label>
                    <p className="mt-1 text-lg font-semibold text-blue-600">
                      {transcriptData.grade && transcriptData.grade !== "-"
                        ? transcriptData.grade
                        : "Not Available"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Scores by Term */}
            {transcriptData.scores && transcriptData.scores.length > 0 ? (
              <div className="space-y-6">
                {Object.entries(groupScoresByTerm(transcriptData.scores)).map(
                  ([term, termScores]) => (
                    <div
                      key={term}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h3 className="text-md font-semibold text-gray-900">
                          {term}
                        </h3>
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Unit Code
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Unit Name
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Term
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                CAT 1
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                CAT 2
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                CAT Total
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Exam
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Grade
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {termScores.map((score) => (
                              <tr
                                key={score.number}
                                className="hover:bg-gray-50"
                              >
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {score.unit_code}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {score.unit_name}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                  {score.course_term}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                  {score.cat_one_score}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                  {score.cat_two_score}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                  {score.cat_total_score}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                                  {score.exam_score}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-gray-900">
                                  {score.total_score}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      score.grade === "A"
                                        ? "bg-green-100 text-green-800"
                                        : score.grade === "B"
                                        ? "bg-blue-100 text-blue-800"
                                        : score.grade === "C"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : score.grade === "D"
                                        ? "bg-orange-100 text-orange-800"
                                        : score.grade === "F"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {score.grade}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden bg-white">
                        <div className="space-y-3 p-4">
                          {termScores.map((score) => (
                            <div
                              key={score.number}
                              className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    {score.unit_code}
                                  </h4>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {score.unit_name}
                                  </p>
                                </div>
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                                    score.grade === "A"
                                      ? "bg-green-100 text-green-800"
                                      : score.grade === "B"
                                      ? "bg-blue-100 text-blue-800"
                                      : score.grade === "C"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : score.grade === "D"
                                      ? "bg-orange-100 text-orange-800"
                                      : score.grade === "F"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {score.grade}
                                </span>
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">CAT 1:</span>
                                  <span className="font-medium text-gray-900">
                                    {score.cat_one_score}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">CAT 2:</span>
                                  <span className="font-medium text-gray-900">
                                    {score.cat_two_score}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">
                                    CAT Total:
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {score.cat_total_score}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Exam:</span>
                                  <span className="font-medium text-gray-900">
                                    {score.exam_score}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-3 pt-3 border-t border-gray-300">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700">
                                    Total Score:
                                  </span>
                                  <span className="text-sm font-bold text-gray-900">
                                    {score.total_score}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <DescriptionOutlined className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No scores available
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No academic scores have been recorded for this course yet.
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
